import React, { useState, useEffect, useRef } from 'react';
import { Image, Home, Palette, Box, Sparkles, Wand2, Upload, XCircle, Loader2, Ruler, User as UserIcon, LogOut, Download, LayoutDashboard, Square, Columns3, Rows3, Sofa, Lightbulb, TreePine, Home as HomeIconOutline, PlusSquare, Building, Car, Umbrella, Tally3, Bed, Bath, Layout, HardHat, Sprout, Camera, KeyRound } from 'lucide-react';

// Firebase imports
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';

// API URLs for Gemini Models
const GEMINI_IMAGE_GENERATION_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-preview-image-generation:generateContent`;
const GEMINI_VISION_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-05-20:generateContent`;

// Hardcoded App ID for production
const appId = 'default-app-id';

// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
    apiKey: "AIzaSyD8nLSpxoUpQ1enHgaXI0BVQLIjwuvN2bU",
    authDomain: "zapps-suite.firebaseapp.com",
    projectId: "zapps-suite",
    storageBucket: "zapps-suite.appspot.com",
    messagingSenderId: "911900476944",
    appId: "1:911900476944:web:376e5a2d20f90b3b736c95",
    measurementId: "G-YE0CBPTZCY"
};

// Helper function for exponential backoff
const exponentialBackoffFetch = async (url, options, retries = 5, delay = 1000) => {
    for (let i = 0; i < retries; i++) {
        try {
            const response = await fetch(url, options);
            if (response.status === 429) { // Too Many Requests
                console.warn(`Rate limit hit. Retrying in ${delay / 1000}s...`);
                await new Promise(res => setTimeout(res, delay));
                delay *= 2; // Double the delay for the next retry
                continue;
            }
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            return response;
        } catch (error) {
            if (i === retries - 1) throw error; // Re-throw if last retry
            console.error(`Fetch error: ${error.message}. Retrying...`);
            await new Promise(res => setTimeout(res, delay));
            delay *= 2;
        }
    }
    throw new Error("Max retries exceeded");
};

// Predefined options for design criteria - MOVED OUTSIDE COMPONENT
const interiorAreas = [
    'Bedroom', 'Living Room', 'Kitchen', 'Bathroom', 'Dining Room',
    'Home Office', 'Restaurant', 'Retail Space'
];

const exteriorAreas = [
    'Facade/Front Yard', 'Backyard/Garden', 'Patio/Deck', 'Swimming Pool Area',
    'Parking Area', 'Balcony', 'Washing Area'
];

const interiorStyles = [
    'Modern', 'Minimalist', 'Scandinavian', 'Bohemian', 'Industrial',
    'Eclectic', 'Traditional', 'Transitional', 'Mid-Century Modern', 'Farmhouse',
    'Coastal', 'Shabby Chic', 'Art Deco', 'Hollywood Glam',
    'Indian Standard', 'Indian Traditional', 'Indian Home Styles' // Added Indian styles
];

const exteriorStyles = [
    'Modern', 'Contemporary', 'Traditional', 'Colonial', 'Victorian',
    'Craftsman', 'Ranch', 'Mediterranean', 'Tudor', 'Farmhouse',
    'Cottage', 'Greek Revival', 'French Country', 'Scandinavian',
    'Indian Standard', 'Indian Traditional', 'Indian Home Styles' // Added Indian styles
];

const commonColorPalettes = [
    'Neutral (Beige, Gray, White)', 'Warm (Earthy Tones, Reds, Oranges)',
    'Cool (Blues, Greens, Purples)', 'Monochromatic', 'Vibrant (Bold, Contrasting)'
];

const commonMaterials = [
    'Wood', 'Metal', 'Glass', 'Concrete', 'Stone', 'Brick', 'Textiles',
    'Natural Fibers (Rattan, Jute)', 'Ceramic/Tile'
];

const wallColors = [
    'White', 'Light Gray', 'Beige', 'Navy Blue', 'Forest Green', 'Terracotta', 'Charcoal', 'Pastel Pink'
];

const ceilingTypes = [
    'Flat', 'Tray', 'Vaulted', 'Coffered', 'Domed', 'Suspended'
];

const floorMaterials = [
    'Hardwood', 'Laminate', 'Vinyl', 'Ceramic Tile', 'Porcelain Tile', 'Carpet', 'Concrete', 'Marble'
];

const floorStyles = [
    'Plank', 'Herringbone', 'Chevron', 'Basket Weave', 'Geometric', 'Large Format', 'Small Tile'
];

// New advanced customization options
const furnitureStyles = [
    'Mid-Century Modern', 'Industrial', 'Minimalist', 'Bohemian', 'Scandinavian',
    'Traditional', 'Contemporary', 'Farmhouse', 'Art Deco', 'Shabby Chic'
];

const lightingFixtures = [
    'Chandelier', 'Pendant Lights', 'Recessed Lighting', 'Track Lighting',
    'Floor Lamps', 'Table Lamps', 'Sconces', 'LED Strips'
];

const landscapingStyles = [
    'Zen Garden', 'Formal Garden', 'Wildflower Meadow', 'Desert Landscape',
    'Tropical Garden', 'Cottage Garden', 'Modern Minimalist', 'Xeriscaping'
];

const roofTypes = [
    'Gable', 'Hip', 'Flat', 'Shed', 'Gambrel', 'Mansard', 'Saltbox'
];

const plotBuildingStyles = [ // Can be same as exterior styles or a unique set
    'Modern', 'Contemporary', 'Traditional', 'Colonial', 'Victorian',
    'Craftsman', 'Mediterranean', 'Farmhouse', 'Industrial', 'Minimalist',
    'Indian Standard', 'Indian Traditional', 'Indian Home Styles' // Added Indian styles
];

const floorPlanFloorTypes = [
    'Ground Floor', 'First Floor', 'Second Floor', 'Third Floor', 'Basement', 'Attic'
];


const App = () => {
    const [apiKey, setApiKey] = useState('');
    const [selectedImage, setSelectedImage] = useState(null); // Stores base64 string now (data URL)
    const [designType, setDesignType] = useState('Interior'); // 'Interior', 'Exterior', 'Plot', or 'Floor Plan Design'
    const [imageInputType, setImageInputType] = useState('plot_photo'); // 'plot_photo' or 'floor_plan' (only for Plot type)
    const [selectedArea, setSelectedArea] = useState('');
    const [designElement, setDesignElement] = useState('Entire Room'); // New state for specific element (Interior only)
    const [designStyle, setDesignStyle] = useState('');
    const [colorPalette, setColorPalette] = useState('');
    const [materials, setMaterials] = useState('');
    const [length, setLength] = useState('');
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState(''); // New state for height

    // States for specific interior elements
    const [wallColor, setWallColor] = useState('');
    const [customWallColor, setCustomWallColor] = useState('');
    const [ceilingType, setCeilingType] = useState('');
    const [customCeilingType, setCustomCeilingType] = useState('');
    const [ceilingColor, setCeilingColor] = useState('');
    const [customCeilingColor, setCustomCeilingColor] = useState('');
    const [floorMaterial, setFloorMaterial] = useState('');
    const [customFloorMaterial, setCustomFloorMaterial] = useState('');
    const [floorStyle, setFloorStyle] = useState('');
    const [customFloorStyle, setCustomFloorStyle] = useState('');

    // New states for advanced customization (Interior/Exterior)
    const [furnitureStyle, setFurnitureStyle] = useState('');
    const [customFurnitureStyle, setCustomFurnitureStyle] = useState('');
    const [lightingFixture, setLightingFixture] = useState('');
    const [customLightingFixture, setCustomLightingFixture] = useState('');
    const [landscapingStyle, setLandscapingStyle] = useState('');
    const [customLandscapingStyle, setCustomLandscapingStyle] = useState('');
    const [roofType, setRoofType] = useState('');
    const [customRoofType, setCustomRoofType] = useState('');

    // New states for Plot-specific options
    const [numFloorsPlot, setNumFloorsPlot] = useState(''); // Number of floors for Plot type
    const [hasBalcony, setHasBalcony] = useState(false);
    const [hasParking, setHasParking] = useState(false);
    const [hasPool, setHasPool] = useState(false);
    const [hasRooftopTerrace, setHasRooftopTerrace] = useState(false);

    // New states for Floor Plan Design specific options
    const [fpNumBedrooms, setFpNumBedrooms] = useState('');
    const [fpNumBathrooms, setFpNumBathrooms] = useState('');
    const [fpHasLivingRoom, setFpHasLivingRoom] = useState(false);
    const [fpHasKitchen, setFpHasKitchen] = useState(false);
    const [fpHasGarage, setFpHasGarage] = useState(false);
    const [fpHasGarden, setFpHasGarden] = useState(false);
    const [floorPlanStyle, setFloorPlanStyle] = useState('');
    const [customFloorPlanStyle, setCustomFloorPlanStyle] = useState('');
    const [additionalFloorPlanNotes, setAdditionalFloorPlanNotes] = useState('');
    const [floorType, setFloorType] = useState(''); // New state for Floor Plan Design's floor type
    const [customFloorType, setCustomFloorType] = useState(''); // New state for custom floor type


    // New state for Virtual Staging Mode
    const [isVirtualStagingMode, setIsVirtualStagingMode] = useState(false);

    // New states for Add/Remove Element feature
    const [showAddRemoveElementModal, setShowAddRemoveElementModal] = useState(false);
    const [addRemovePrompt, setAddRemovePrompt] = useState('');

    // Plot-specific generated image states for iterative views
    const [plotFrontViewImage, setPlotFrontViewImage] = useState(null); // Stores the base front view for consistency
    const [plotImageDescription, setPlotImageDescription] = useState(''); // Store initial image description for consistency


    const [generatedDesignImage, setGeneratedDesignImage] = useState(null); // Holds the currently displayed image (single view)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Firebase states
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [userCredits, setUserCredits] = useState(0);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // Camera states and ref
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [videoStream, setVideoStream] = useState(null);
    const videoRef = useRef(null);


    // Initialize Firebase and set up auth listener
    useEffect(() => {
        const app = initializeApp(firebaseConfig);
        const authInstance = getAuth(app);
        const dbInstance = getFirestore(app);

        setDb(dbInstance);
        setAuth(authInstance);

        const unsubscribe = onAuthStateChanged(authInstance, (user) => {
            if (user) {
                setCurrentUser(user);
                setUserId(user.uid);
            } else {
                // User is signed out
                setCurrentUser(null);
                setUserId(null);
            }
            setIsAuthReady(true); // Auth state is now known (either logged in or out)
        });

        return () => unsubscribe(); // Cleanup subscription
    }, []);

    // Fetch or initialize user credits when auth is ready and userId is set
    useEffect(() => {
        if (isAuthReady && db && userId) {
            const userProfileRef = doc(db, 'artifacts', appId, 'users', userId, 'user_data', 'profile');

            const unsubscribe = onSnapshot(userProfileRef, (docSnap) => {
                if (docSnap.exists()) {
                    setUserCredits(docSnap.data().credits);
                } else {
                    // New user, initialize credits to 30
                    setDoc(userProfileRef, { credits: 30 })
                        .then(() => setUserCredits(30))
                        .catch(e => console.error("Error setting initial credits:", e));
                }
            }, (error) => {
                console.error("Error listening to credits:", error);
                setError("Failed to load user credits.");
            });

            return () => unsubscribe(); // Cleanup snapshot listener
        }
    }, [isAuthReady, db, userId]);

    // Reset specific element options and advanced options when design type changes
    useEffect(() => {
        setSelectedArea('');
        setDesignStyle('');
        setColorPalette(''); // Reset color palette
        setMaterials(''); // Reset materials
        setLength('');
        setWidth('');
        setHeight(''); // Reset height
        setDesignElement('Entire Room'); // Reset to entire room when type changes
        setIsVirtualStagingMode(false); // Reset virtual staging mode

        // Reset all specific interior/exterior/plot options
        setWallColor('');
        setCustomWallColor('');
        setCeilingType('');
        setCustomCeilingType('');
        setCeilingColor('');
        setCustomCeilingColor('');
        setFloorMaterial('');
        setCustomFloorMaterial('');
        setFloorStyle('');
        setCustomFloorStyle('');
        setFurnitureStyle('');
        setCustomFurnitureStyle('');
        setLightingFixture('');
        setCustomLightingFixture('');
        setLandscapingStyle('');
        setCustomLandscapingStyle('');
        setRoofType('');
        setCustomRoofType('');

        // Reset Plot-specific states
        setNumFloorsPlot('');
        setHasBalcony(false);
        setHasParking(false);
        setHasPool(false);
        setHasRooftopTerrace(false);

        // Reset Floor Plan Design specific states
        setFpNumBedrooms('');
        setFpNumBathrooms('');
        setFpHasLivingRoom(false);
        setFpHasKitchen(false);
        setFpHasGarage(false);
        setFpHasGarden(false);
        setFloorPlanStyle('');
        setCustomFloorPlanStyle('');
        setAdditionalFloorPlanNotes('');
        setFloorType(''); // Reset floor type
        setCustomFloorType(''); // Reset custom floor type


        // Clear generated images and plot-specific image states when design type changes
        setGeneratedDesignImage(null);
        setPlotFrontViewImage(null);
        setPlotImageDescription('');
        setImageInputType('plot_photo'); // Reset image input type for Plot

    }, [designType]);

    // Reset specific element customizations when design element changes
    useEffect(() => {
        setWallColor('');
        setCustomWallColor('');
        setCeilingType('');
        setCustomCeilingType('');
        setCeilingColor('');
        setCustomCeilingColor('');
        setFloorMaterial('');
        setCustomFloorMaterial('');
        setFloorStyle('');
        setCustomFloorStyle('');
    }, [designElement]);

    // Effect to handle the video stream
    useEffect(() => {
        if (isCameraOpen && videoStream && videoRef.current) {
            videoRef.current.srcObject = videoStream;
        }
    }, [isCameraOpen, videoStream]);


    // Handles image file selection and converts it to Base64
    const handleImageChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const reader = new FileReader();

            reader.onloadend = () => {
                // reader.result contains the Base64 encoded string (data URL)
                setSelectedImage(reader.result);
                setError(''); // Clear any previous errors
            };

            reader.onerror = () => {
                setError('Failed to read image file.');
                setSelectedImage(null);
            };

            reader.readAsDataURL(file); // Read the file as a Data URL (Base64)
        }
    };

    const handleOpenCamera = async () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                setVideoStream(stream);
                setIsCameraOpen(true);
            } catch (err) {
                console.error("Error accessing camera:", err);
                setError("Could not access the camera. Please check permissions.");
            }
        } else {
            setError("Camera not supported on this device.");
        }
    };

    const handleCloseCamera = () => {
        if (videoStream) {
            videoStream.getTracks().forEach(track => track.stop());
        }
        setVideoStream(null);
        setIsCameraOpen(false);
    };

    const handleCapturePhoto = () => {
        if (videoRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const context = canvas.getContext('2d');
            context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/png');
            setSelectedImage(dataUrl);
            handleCloseCamera();
        }
    };

    const generateDesignPrompt = (currentImageDescription, viewAngle = 'front') => { // Added viewAngle parameter
        let prompt = '';
        let sizeDescription = '';
        if (length && width && height) {
            sizeDescription = ` with approximate dimensions of ${length}x${width}x${height} sqft`;
        } else if (length && width) {
            sizeDescription = ` with approximate dimensions of ${length}x${width} sqft`;
        }

        // Common elements for all types
        const commonStyleMaterials = `Apply a "${designStyle || 'modern'}" style. Use a "${colorPalette || 'neutral'}" color scheme. Incorporate materials like "${materials || 'wood and concrete'}".`;

        if (designType === 'Interior') {
            const effectiveWallColor = wallColor === 'Other' ? customWallColor : wallColor;
            const effectiveCeilingType = ceilingType === 'Other' ? customCeilingType : ceilingType;
            const effectiveCeilingColor = ceilingColor === 'Other' ? customCeilingColor : ceilingColor;
            const effectiveFloorMaterial = floorMaterial === 'Other' ? customFloorMaterial : floorMaterial;
            const effectiveFloorStyle = floorStyle === 'Other' ? customFloorStyle : floorStyle;
            const effectiveFurnitureStyle = furnitureStyle === 'Other' ? customFurnitureStyle : furnitureStyle;
            const effectiveLightingFixture = lightingFixture === 'Other' ? customLightingFixture : lightingFixture;

            let specificElementInstruction = '';
            switch (designElement) {
                case 'Wall':
                    specificElementInstruction = `Focus specifically on redesigning the walls. Apply a wall color of "${effectiveWallColor || 'a suitable color'}" to the existing walls.`;
                    break;
                case 'Floor':
                    specificElementInstruction = `Focus specifically on redesigning the floor. Use "${effectiveFloorMaterial || 'a suitable material'}" flooring with a "${effectiveFloorStyle || 'standard'}" style.`;
                    break;
                case 'Ceiling':
                    specificElementInstruction = `Focus specifically on redesigning the ceiling. Implement a "${effectiveCeilingType || 'flat'}" ceiling type with a color of "${effectiveCeilingColor || 'a suitable color'}".`;
                    break;
                case 'Entire Room':
                default:
                    specificElementInstruction = isVirtualStagingMode
                        ? `Furnish and stage this empty ${selectedArea ? selectedArea.toLowerCase() : 'room'} space. `
                        : `Redesign the entire ${selectedArea ? selectedArea.toLowerCase() : 'room'} space.`;
                    break;
            }

            let advancedInteriorInstruction = '';
            if (designElement === 'Entire Room') { // Advanced options only for entire room redesign
                if (effectiveFurnitureStyle) {
                    advancedInteriorInstruction += ` Incorporate furniture in a "${effectiveFurnitureStyle}" style.`;
                }
                if (effectiveLightingFixture) {
                    advancedInteriorInstruction += ` Use "${effectiveLightingFixture}" as the primary lighting fixture.`;
                }
            }

            prompt = `
                ${specificElementInstruction}
                Strictly preserve the core layout and architectural features as described:
                "${currentImageDescription}"

                ${commonStyleMaterials}
                This is a ${selectedArea ? selectedArea.toLowerCase() : 'interior'} space${sizeDescription}.${advancedInteriorInstruction}
                The output should be a photorealistic rendering of the redesigned space, maintaining the original perspective and structure.
            `;
        } else if (designType === 'Exterior') {
            const effectiveLandscapingStyle = landscapingStyle === 'Other' ? customLandscapingStyle : landscapingStyle;
            const effectiveRoofType = roofType === 'Other' ? customRoofType : roofType;

            let advancedExteriorInstruction = '';
            if (effectiveLandscapingStyle) {
                advancedExteriorInstruction += ` Design the landscaping in a "${effectiveLandscapingStyle}" style.`;
            }
            if (effectiveRoofType) {
                advancedExteriorInstruction += ` Feature a "${effectiveRoofType}" roof type.`;
            }

            prompt = `
                Redesign this exterior ${selectedArea ? selectedArea.toLowerCase() : 'space'}.
                Strictly preserve the core layout and architectural features as described:
                "${currentImageDescription}"

                ${commonStyleMaterials}
                This is an exterior ${selectedArea ? selectedArea.toLowerCase() : 'space'}${sizeDescription}.${advancedExteriorInstruction}
                The output should be a photorealistic rendering of the redesigned exterior, maintaining the original perspective and structure.
            `;
        } else if (designType === 'Plot') {
            let buildingFeatures = [];
            if (hasBalcony) buildingFeatures.push('balconies');
            if (hasParking) buildingFeatures.push('a parking area');
            if (hasPool) buildingFeatures.push('a swimming pool');
            if (hasRooftopTerrace) buildingFeatures.push('a rooftop terrace');

            const buildingFeaturesDescription = buildingFeatures.length > 0
                ? ` It should include ${buildingFeatures.join(', ')}.`
                : '';

            let viewSpecificPrompt = '';
            switch (viewAngle) {
                case 'front':
                    viewSpecificPrompt = 'Generate a photorealistic rendering of the building from the **front side**, integrating seamlessly with the surrounding environment.';
                    break;
                case 'back':
                    viewSpecificPrompt = 'Generate a photorealistic rendering of the building from the **back side**, integrating seamlessly with the surrounding environment.';
                    break;
                case 'side':
                    viewSpecificPrompt = 'Generate a photorealistic rendering of the building from a **side view**, integrating seamlessly with the surrounding environment.';
                    break;
                case 'top':
                    viewSpecificPrompt = 'Generate a photorealistic rendering of the building from a **top-down aerial view**, integrating seamlessly with the surrounding environment.';
                    break;
                default:
                    viewSpecificPrompt = 'Generate a photorealistic rendering of the building, integrating seamlessly with the surrounding environment.';
            }


            if (imageInputType === 'floor_plan') {
                prompt = `
                    Construct a 3D building exterior based on this 2D floor plan. The floor plan is described as: "${currentImageDescription}"
                    The building should have ${numFloorsPlot || '2'} floors${sizeDescription}.
                    ${commonStyleMaterials}
                    The building should be in a "${designStyle || 'modern'}" style.
                    ${buildingFeaturesDescription}
                    ${viewSpecificPrompt}
                `;
            } else { // plot_photo
                prompt = `
                    Design a new building on this empty plot area.
                    The plot is described as: "${currentImageDescription}"

                    Construct a building with ${numFloorsPlot || '2'} floors${sizeDescription}.
                    ${commonStyleMaterials}
                    The building should be in a "${designStyle || 'modern'}" style.
                    ${buildingFeaturesDescription}
                    ${viewSpecificPrompt}
                `;
            }
        } else if (designType === 'Floor Plan Design') {
            const effectiveFloorPlanStyle = floorPlanStyle === 'Other' ? customFloorPlanStyle : floorPlanStyle;
            const effectiveFloorType = floorType === 'Other' ? customFloorType : floorType; // Get effective floor type

            let roomsDescription = [];
            if (fpNumBedrooms) roomsDescription.push(`${fpNumBedrooms} bedrooms`);
            if (fpNumBathrooms) roomsDescription.push(`${fpNumBathrooms} bathrooms`);
            if (fpHasLivingRoom) roomsDescription.push('a living room');
            if (fpHasKitchen) roomsDescription.push('a kitchen');
            if (fpHasGarage) roomsDescription.push('a garage');
            if (fpHasGarden) roomsDescription.push('a garden');

            const roomsText = roomsDescription.length > 0 ? ` with ${roomsDescription.join(', ')}` : '';
            const notesText = additionalFloorPlanNotes.trim() ? ` Additional notes: ${additionalFloorPlanNotes.trim()}.` : '';

            prompt = `
                Generate a detailed 2D floor plan image for a home.
                This is the ${effectiveFloorType || 'Ground Floor'}.
                The dimensions are ${length || 'unspecified'}ft length and ${width || 'unspecified'}ft width.
                The floor plan should be in a "${effectiveFloorPlanStyle || 'modern'}" style.
                It should include${roomsText}.${notesText}
                The output should be a clear, top-down architectural drawing of the floor plan.
            `;
        }

        return prompt;
    };


    const handleGenerateDesign = async () => {
        if (!apiKey) {
            setError('Please enter your Gemini API key to proceed.');
            return;
        }
        const cost = (designType === 'Floor Plan Design') ? 2 : 3; // Floor Plan Design costs 2 credits, others cost 3

        if (!selectedImage && designType !== 'Floor Plan Design') { // Image is optional for Floor Plan Design
            setError('Please upload an image first.');
            return;
        }
        if (!userId) {
            setError('Authentication not ready. Please wait a moment or refresh.');
            return;
        }
        if (userCredits < cost) {
            setError(`Not enough credits! ${cost} credits are required to generate this design.`);
            return;
        }
        if (designType === 'Plot' && !numFloorsPlot) { // Check numFloorsPlot for Plot type
            setError('Please specify the number of floors for the building on the plot.');
            return;
        }
        if (designType === 'Floor Plan Design' && (!length || !width || !floorType || (!fpNumBedrooms && !fpNumBathrooms && !fpHasLivingRoom && !fpHasKitchen && !fpHasGarage && !fpHasGarden && !additionalFloorPlanNotes.trim()))) {
            setError('Please provide details for the floor plan design (e.g., dimensions, floor type, rooms, features, or notes).');
            return;
        }


        setLoading(true);
        setGeneratedDesignImage(null); // Clear previous single image
        setError(''); // Clear previous error

        const base64ImageData = selectedImage ? selectedImage.split(',')[1] : null; // Get only the base64 data, can be null
        const mimeType = selectedImage ? selectedImage.substring(selectedImage.indexOf(':') + 1, selectedImage.indexOf(';')) : 'image/png'; // Default mime type if no image

        try {
            let imageDescription = '';
            if (designType !== 'Floor Plan Design') { // Only analyze image if not generating floor plan from scratch
                // --- Step 1: Analyze the image with Gemini Vision Model ---
                // Adjust vision prompt based on design type
                const visionPromptBase = designType === 'Plot' && imageInputType === 'floor_plan'
                    ? `Describe this 2D floor plan image in detail, focusing on its layout, room divisions, and architectural elements.`
                    : (designType === 'Plot' && imageInputType === 'plot_photo'
                        ? `Describe this empty plot area in detail, focusing on its terrain, existing elements (e.g., trees, fences), and surrounding environment. Be very precise about its current state and features.`
                        : (isVirtualStagingMode
                            ? `Describe this empty ${designType.toLowerCase()} space in detail, focusing on its current architectural features, layout, and potential for furnishing. Be very precise about shapes, positions, and structural components like windows, doors, and walls.`
                            : `Describe this ${designType.toLowerCase()} space in detail, focusing on its current layout, architectural features, existing furniture/elements, and overall ambiance. Be very precise about shapes, positions, and structural components.`));

                const visionPayload = {
                    contents: [
                        {
                            role: "user",
                            parts: [
                                { text: visionPromptBase },
                                {
                                    inlineData: {
                                        mimeType: mimeType,
                                        data: base64ImageData
                                    }
                                }
                            ]
                        }
                    ]
                };

                const visionResponse = await exponentialBackoffFetch(`${GEMINI_VISION_API_URL}?key=${apiKey}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(visionPayload)
                });

                const visionResult = await visionResponse.json();
                imageDescription = visionResult?.candidates?.[0]?.content?.parts?.[0]?.text;

                if (!imageDescription) {
                    setError('Failed to get a detailed description from the AI. Please try again.');
                    setLoading(false);
                    return;
                }
                setPlotImageDescription(imageDescription); // Store for subsequent plot views
            } else {
                // For Floor Plan Design, no image is uploaded, so description is based on form inputs
                const effectiveFloorPlanStyle = floorPlanStyle === 'Other' ? customFloorPlanStyle : floorPlanStyle;
                const effectiveFloorType = floorType === 'Other' ? customFloorType : floorType;
                let roomsDescription = [];
                if (fpNumBedrooms) roomsDescription.push(`${fpNumBedrooms} bedrooms`);
                if (fpNumBathrooms) roomsDescription.push(`${fpNumBathrooms} bathrooms`);
                if (fpHasLivingRoom) roomsDescription.push('a living room');
                if (fpHasKitchen) roomsDescription.push('a kitchen');
                if (fpHasGarage) roomsDescription.push('a garage');
                if (fpHasGarden) roomsDescription.push('a garden');
                const roomsText = roomsDescription.length > 0 ? ` with ${roomsDescription.join(', ')}` : '';
                const notesText = additionalFloorPlanNotes.trim() ? ` Additional notes: ${additionalFloorPlanNotes.trim()}.` : '';

                imageDescription = `A floor plan for a ${effectiveFloorType || 'Ground Floor'} with ${numFloorsPlot || '1'} floor(s), dimensions ${length}x${width} sqft. It has ${roomsText}.${notesText} Style: ${effectiveFloorPlanStyle}.`;
            }


            // --- Step 2: Generate/Edit image with Gemini 2.0 Flash Image Generation model ---
            let generatedImageUrl = null;
            let imageGenPayload = {};

            if (designType === 'Floor Plan Design') {
                const floorPlanPrompt = generateDesignPrompt(imageDescription);
                imageGenPayload = {
                    contents: [
                        { parts: [{ text: floorPlanPrompt }] } // No inlineData for floor plan generation from scratch
                    ],
                    generationConfig: { 
                        responseModalities: ['TEXT', 'IMAGE']
                    },
                };
            } else { // Interior, Exterior, Plot (initial front view)
                const initialDesignPrompt = generateDesignPrompt(imageDescription, 'front');
                imageGenPayload = {
                    contents: [
                        { parts: [{ text: initialDesignPrompt }, { inlineData: { mimeType: mimeType, data: base64ImageData } }] }
                    ],
                    generationConfig: { 
                        responseModalities: ['TEXT', 'IMAGE']
                    },
                };
            }

            const geminiImageGenResponse = await exponentialBackoffFetch(`${GEMINI_IMAGE_GENERATION_API_URL}?key=${apiKey}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(imageGenPayload)
            });

            const geminiImageGenResult = await geminiImageGenResponse.json();
            const generatedBase64Data = geminiImageGenResult?.candidates?.[0]?.content?.parts?.find(p => p.inlineData)?.inlineData?.data;

            if (generatedBase64Data) {
                generatedImageUrl = `data:image/png;base64,${generatedBase64Data}`;
                setGeneratedDesignImage(generatedImageUrl); // Set the generated image as the main displayed image
                if (designType === 'Plot') {
                    setPlotFrontViewImage(generatedImageUrl); // Store the front view for future plot views
                }
            } else {
                setError('Failed to generate the design. The AI might not have been able to process the request or generate a suitable image. Please try a different image or adjust your preferences.');
                console.error('Unexpected Gemini Image Generation API response structure or missing image data:', geminiImageGenResult);
                setLoading(false);
                return;
            }

            // --- Deduct credits ONLY AFTER successful generation ---
            const userProfileRef = doc(db, 'artifacts', appId, 'users', userId, 'user_data', 'profile');
            await updateDoc(userProfileRef, { credits: userCredits - cost });

        } catch (err) {
            console.error('Error during design generation process:', err);
            setError(`Error generating design: ${err.message}. Please ensure your API key is correct and try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateAdditionalPlotView = async (viewAngle) => {
        const cost = 3; // Additional views cost 3 credits

        if (!plotFrontViewImage || !plotImageDescription) {
            setError('Please generate the front view first.');
            return;
        }
        if (userCredits < cost) {
            setError(`Not enough credits! ${cost} credits are required for this operation.`);
            return;
        }

        setLoading(true);
        setError('');

        // Use the stored plotFrontViewImage as the base image for consistency
        const base64ReferenceImageData = plotFrontViewImage.split(',')[1];
        const mimeType = plotFrontViewImage.substring(plotFrontViewImage.indexOf(':') + 1, plotFrontViewImage.indexOf(';'));

        try {
            // Deduct credits first
            const userProfileRef = doc(db, 'artifacts', appId, 'users', userId, 'user_data', 'profile');
            await updateDoc(userProfileRef, { credits: userCredits - cost });
            setUserCredits(prev => prev - cost); // Optimistic UI update

            const additionalViewPrompt = generateDesignPrompt(plotImageDescription, viewAngle);

            const geminiImageGenPayload = {
                contents: [
                    {
                        parts: [
                            { text: additionalViewPrompt },
                            {
                                inlineData: {
                                    mimeType: mimeType,
                                    data: base64ReferenceImageData // Use the front view as reference
                                }
                            }
                        ]
                    }
                ],
                generationConfig: {
                    responseModalities: ['TEXT', 'IMAGE']
                },
            };

            const geminiImageGenResponse = await exponentialBackoffFetch(`${GEMINI_IMAGE_GENERATION_API_URL}?key=${apiKey}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(geminiImageGenPayload)
            });

            const geminiImageGenResult = await geminiImageGenResponse.json();
            const generatedBase64Data = geminiImageGenResult?.candidates?.[0]?.content?.parts?.find(p => p.inlineData)?.inlineData?.data;

            if (generatedBase64Data) {
                const imageUrl = `data:image/png;base64,${generatedBase64Data}`;
                setGeneratedDesignImage(imageUrl); // Display the new view
            } else {
                setError(`Failed to generate ${viewAngle} view. Please try again.`);
                console.error(`Unexpected Gemini Image Generation API response structure or missing image data for ${viewAngle} view:`, geminiImageGenResult);
                // Revert credits if generation fails
                await updateDoc(userProfileRef, { credits: userCredits + cost });
                setUserCredits(prev => prev + cost);
            }
        } catch (err) {
            console.error(`Error generating ${viewAngle} view:`, err);
            setError(`Error generating ${viewAngle} view: ${err.message}. Please try again.`);
            // Revert credits if any other error occurs
            const userProfileRef = doc(db, 'artifacts', appId, 'users', userId, 'user_data', 'profile');
            await updateDoc(userProfileRef, { credits: userCredits }); // Revert to original credits
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        if (auth) {
            try {
                await signOut(auth);
                // Optionally, clear user data or redirect
                setCurrentUser(null);
                setUserId(null);
                setUserCredits(0);
                setSelectedImage(null);
                setGeneratedDesignImage(null);
                setPlotFrontViewImage(null); // Clear plot specific image states
                setPlotImageDescription('');
                setError('');
                console.log("User logged out successfully.");
            } catch (error) {
                console.error("Error logging out:", error);
                setError("Failed to log out. Please try again.");
            }
        }
    };

    const handleDownloadDesign = (imageUrl) => {
        if (imageUrl) {
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = `ai_design_${Date.now()}.png`; // Unique filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleAddRemoveElement = async () => {
        const cost = 2; // Add/Remove Element cost 2 credits

        if (!generatedDesignImage) { // Only allow add/remove on the currently displayed image
            setError('Please generate a design first to add/remove elements.');
            return;
        }
        if (!addRemovePrompt.trim()) {
            setError('Please type what you want to add or remove.');
            return;
        }
        if (userCredits < cost) {
            setError(`Not enough credits! ${cost} credits are required for this operation.`);
            return;
        }

        setLoading(true);
        setError('');

        const base64CurrentDesign = generatedDesignImage.split(',')[1];
        const mimeType = generatedDesignImage.substring(generatedDesignImage.indexOf(':') + 1, generatedDesignImage.indexOf(';'));

        try {
            // Deduct credits
            const userProfileRef = doc(db, 'artifacts', appId, 'users', userId, 'user_data', 'profile');
            await updateDoc(userProfileRef, { credits: userCredits - cost });
            setUserCredits(prev => prev - cost); // Optimistic UI update

            const iterativePrompt = `Based on the current image, ${addRemovePrompt}. Maintain the overall style and structure of the image.`;

            const geminiImageGenPayload = {
                contents: [
                    {
                        parts: [
                            { text: iterativePrompt },
                            {
                                inlineData: {
                                    mimeType: mimeType,
                                    data: base64CurrentDesign
                                }
                            }
                        ]
                    }
                ],
                generationConfig: {
                    responseModalities: ['TEXT', 'IMAGE']
                },
            };

            const geminiImageGenResponse = await exponentialBackoffFetch(`${GEMINI_IMAGE_GENERATION_API_URL}?key=${apiKey}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(geminiImageGenPayload)
            });

            const geminiImageGenResult = await geminiImageGenResponse.json();
            const generatedBase64Data = geminiImageGenResult?.candidates?.[0]?.content?.parts?.find(p => p.inlineData)?.inlineData?.data;

            if (generatedBase64Data) {
                const imageUrl = `data:image/png;base64,${generatedBase64Data}`;
                setGeneratedDesignImage(imageUrl); // Update the displayed image with the new one
                setAddRemovePrompt(''); // Clear the input field
                setShowAddRemoveElementModal(false); // Close the modal
            } else {
                setError('Failed to modify the design. Please try a different prompt.');
                console.error('Unexpected Gemini Image Generation API response structure or missing image data:', geminiImageGenResult);
                // Revert credits if generation fails
                await updateDoc(userProfileRef, { credits: userCredits + cost });
                setUserCredits(prev => prev + cost);
            }
        } catch (err) {
            console.error('Error during add/remove element process:', err);
            setError(`Error: ${err.message}. Please try again.`);
            // Revert credits if any other error occurs
            const userProfileRef = doc(db, 'artifacts', appId, 'users', userId, 'user_data', 'profile');
            await updateDoc(userProfileRef, { credits: userCredits }); // Revert to original credits
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        if (!auth) return;
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
            // onAuthStateChanged will handle setting the user state
        } catch (error) {
            console.error("Error during Google sign-in:", error);
            setError("Failed to sign in with Google. Please try again.");
        }
    };


    if (!isAuthReady) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <Loader2 className="animate-spin w-12 h-12 text-indigo-600" />
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200 p-4">
                <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                    <Sparkles className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-indigo-700 mb-2">Welcome to Zenifex Design Studio V 1.0</h2>
                    <p className="text-gray-600 mb-8">Please sign in with your Google account to continue.</p>
                    <button
                        onClick={handleGoogleSignIn}
                        className="w-full inline-flex justify-center items-center gap-3 py-3 px-4 bg-white hover:bg-gray-100 text-gray-700 font-semibold rounded-lg border border-gray-300 shadow-sm transition-colors duration-300"
                    >
                        <svg className="w-6 h-6" viewBox="0 0 48 48">
                            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
                            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
                            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
                            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C42.022,35.244,44,30.036,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
                        </svg>
                        Sign in with Google
                    </button>
                    {error && <p className="text-red-500 mt-4">{error}</p>}
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-200 p-4 sm:p-8 font-inter text-gray-800">
            <style>
                {`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
                body { font-family: 'Inter', sans-serif; }
                .scrollable-content {
                    max-height: calc(100vh - 200px); /* Adjust based on header/footer height */
                    overflow-y: auto;
                }
                /* Hide number input arrows */
                input[type='number']::-webkit-inner-spin-button,
                input[type='number']::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                input[type='number'] {
                    -moz-appearance: textfield;
                }
                /* Modal styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.7);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }
                .modal-content {
                    background: white;
                    padding: 2rem;
                    border-radius: 0.75rem;
                    max-width: 90%;
                    max-height: 90%;
                    overflow-y: auto;
                    position: relative;
                }
                .close-button {
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: #4b5563;
                }
                `}
            </style>

            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8">
                <h1 className="text-3xl sm:text-4xl font-bold text-center text-indigo-700 mb-8 flex items-center justify-center gap-3">
                    <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-500" />
                    Zenifex Design Studio
                </h1>

                {/* API Key Input */}
                 <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <label htmlFor="apiKey" className="block text-lg font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <KeyRound className="w-5 h-5" /> Licence Key (to activate)
                    </label>
                    <input
                        id="apiKey"
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="Contact Zenifex IT Solutions to get a key"
                        className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                    />
                </div>

                {/* User Info and Credits */}
                {isAuthReady && (
                    <div className="flex flex-col sm:flex-row justify-between items-center mb-6 p-4 bg-gray-100 rounded-lg shadow-inner text-gray-700 text-sm sm:text-base">
                        <div className="flex items-center gap-2 mb-2 sm:mb-0">
                            <UserIcon className="w-5 h-5 text-indigo-600" />
                            <span className="truncate">User ID: **{userId || 'Loading...'}**</span>
                        </div>
                        <div className="flex items-center gap-2 font-semibold">
                            <span className="text-indigo-600">Credits:</span>
                            <span className="text-green-600 text-lg">{userCredits}</span>
                            {currentUser && ( // Show logout button only if a user is actively logged in
                                <>
								<a href="https://zenifex.in/credits" className="ml-2 px-3 py-1 bg-blue-500 text-white rounded-full text-sm hover:bg-green-600 transition-colors duration-200 flex items-center gap-1">Buy Credits</a>
                                    <button
                                        onClick={handleLogout}
                                        className="ml-2 px-3 py-1 bg-red-500 text-white rounded-full text-sm
                                                   hover:bg-red-600 transition-colors duration-200 flex items-center gap-1"
                                    >
                                        <LogOut className="w-4 h-4" /> Logout
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
                {!isAuthReady && (
                    <div className="text-center text-gray-500 mb-6">Authenticating...</div>
                )}

                {/* Image Upload Section */}
                <div className="mb-8 p-6 bg-indigo-50 rounded-lg border border-indigo-200">
                    <h2 className="text-xl sm:text-2xl font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <Image className="w-6 h-6" /> Upload Your Space
                    </h2>
                    <p className="text-sm text-gray-600 mb-3">
                        You can select a picture from your device or use your smartphone camera to capture one directly!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="block w-full text-sm text-gray-700
                                       file:mr-4 file:py-2 file:px-4
                                       file:rounded-full file:border-0
                                       file:text-sm file:font-semibold
                                       file:bg-indigo-100 file:text-indigo-700
                                       hover:file:bg-indigo-200 cursor-pointer"
                        />
                         <button
                            onClick={handleOpenCamera}
                            className="w-full sm:w-auto flex-shrink-0 px-4 py-2 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                            <Camera className="w-5 h-5" /> Open Camera
                        </button>
                    </div>
                    {selectedImage && (
                        <div className="mt-4 relative group">
                            <img
                                src={selectedImage}
                                alt="Uploaded preview"
                                className="w-full h-48 object-cover rounded-lg shadow-md border border-gray-200"
                            />
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full
                                           hover:bg-red-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                                aria-label="Remove image"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Design Criteria Section */}
                <div className="mb-8 p-6 bg-green-50 rounded-lg border border-green-200">
                    <h2 className="text-xl sm:text-2xl font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <Wand2 className="w-6 h-6" /> Customize Your Design
                    </h2>

                    {/* Design Type */}
                    <div className="mb-5">
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                            <Home className="inline-block mr-2" /> Design For:
                        </label>
                        <div className="flex flex-wrap gap-4">
                            <button
                                onClick={() => setDesignType('Interior')}
                                className={`px-5 py-2 rounded-full text-lg font-medium transition-all duration-200
                                            ${designType === 'Interior'
                                                ? 'bg-green-600 text-white shadow-md'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'}`
                                          }
                            >
                                Interior
                            </button>
                            <button
                                onClick={() => setDesignType('Exterior')}
                                className={`px-5 py-2 rounded-full text-lg font-medium transition-all duration-200
                                            ${designType === 'Exterior'
                                                ? 'bg-green-600 text-white shadow-md'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'}`
                                          }
                            >
                                Exterior
                            </button>
                            <button
                                onClick={() => setDesignType('Plot')}
                                className={`px-5 py-2 rounded-full text-lg font-medium transition-all duration-200
                                            ${designType === 'Plot'
                                                ? 'bg-green-600 text-white shadow-md'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'}`
                                          }
                            >
                                Plot
                            </button>
                            <button
                                onClick={() => setDesignType('Floor Plan Design')}
                                className={`px-5 py-2 rounded-full text-lg font-medium transition-all duration-200
                                            ${designType === 'Floor Plan Design'
                                                ? 'bg-green-600 text-white shadow-md'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'}`
                                          }
                            >
                                Floor Plan Design
                            </button>
                        </div>
                    </div>

                    {/* Image Input Type for Plot */}
                    {designType === 'Plot' && (
                        <div className="mb-5 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <label className="block text-lg font-medium text-gray-700 mb-2">
                                <Image className="inline-block mr-2" /> Image Input Type:
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="plotImageType"
                                        value="plot_photo"
                                        checked={imageInputType === 'plot_photo'}
                                        onChange={() => setImageInputType('plot_photo')}
                                        className="form-radio h-4 w-4 text-blue-600 mr-2"
                                    />
                                    Plot Photo
                                </label>
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="plotImageType"
                                        value="floor_plan"
                                        checked={imageInputType === 'floor_plan'}
                                        onChange={() => setImageInputType('floor_plan')}
                                        className="form-radio h-4 w-4 text-blue-600 mr-2"
                                    />
                                    Floor Plan
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Specific Area (Hidden for Floor Plan Design) */}
                    {designType !== 'Floor Plan Design' && (
                        <div className="mb-5">
                            <label htmlFor="selectedArea" className="block text-lg font-medium text-gray-700 mb-2">
                                <Sparkles className="inline-block mr-2" /> Specific Area:
                            </label>
                            <select
                                id="selectedArea"
                                value={selectedArea}
                                onChange={(e) => setSelectedArea(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                            >
                                <option value="">Select a specific area (Optional)</option>
                                {designType === 'Interior'
                                    ? interiorAreas.map(area => <option key={area} value={area}>{area}</option>)
                                    : exteriorAreas.map(area => <option key={area} value={area}>{area}</option>)
                                }
                                {/* Plot type does not have predefined areas, but user can type in custom area via 'Other' */}
                                {designType === 'Plot' && <option value="Residential Plot">Residential Plot</option>}
                                {designType === 'Plot' && <option value="Commercial Plot">Commercial Plot</option>}
                                {designType === 'Plot' && <option value="Industrial Plot">Industrial Plot</option>}
                                {designType === 'Plot' && <option value="Other">Other...</option>}
                            </select>
                            {designType === 'Plot' && selectedArea === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom plot area (e.g., 'Vacant Land')"
                                    value={selectedArea === 'Other' ? '' : selectedArea} // Clear if switching from predefined to Other
                                    onChange={(e) => setSelectedArea(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                                />
                            )}
                        </div>
                    )}

                    {/* Dimensions (Moved and adapted for Floor Plan Design) */}
                    {designType !== 'Floor Plan Design' ? ( // Dimensions for Interior/Exterior/Plot Photo
                        <div className="mb-5">
                            <label className="block text-lg font-medium text-gray-700 mb-2">
                                <Ruler className="inline-block mr-2" /> Space Dimensions (sqft):
                            </label>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <input
                                    type="number"
                                    placeholder="Length"
                                    value={length}
                                    onChange={(e) => setLength(e.target.value)}
                                    className="w-full sm:w-1/3 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                                />
                                <input
                                    type="number"
                                    placeholder="Width"
                                    value={width}
                                    onChange={(e) => setWidth(e.target.value)}
                                    className="w-full sm:w-1/3 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                                />
                                <input
                                    type="number"
                                    placeholder="Height"
                                    value={height}
                                    onChange={(e) => setHeight(e.target.value)}
                                    className="w-full sm:w-1/3 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                                />
                            </div>
                        </div>
                    ) : ( // Dimensions for Floor Plan Design
                        <div className="mb-5">
                            <label className="block text-lg font-medium text-gray-700 mb-2">
                                <Ruler className="inline-block mr-2" /> Floor Plan Dimensions (sqft):
                            </label>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <input
                                    type="number"
                                    placeholder="Length"
                                    value={length}
                                    onChange={(e) => setLength(e.target.value)}
                                    className="w-full sm:w-1/2 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                                />
                                <input
                                    type="number"
                                    placeholder="Width"
                                    value={width}
                                    onChange={(e) => setWidth(e.target.value)}
                                    className="w-full sm:w-1/2 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                                />
                            </div>
                        </div>
                    )}

                    {/* Floor Plan Design Specific Options */}
                    {designType === 'Floor Plan Design' && (
                        <div className="mb-5 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h3 className="text-lg font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <Layout className="w-5 h-5" /> Floor Plan Details:
                            </h3>
                            <label htmlFor="floorType" className="block text-base font-medium text-gray-700 mb-2 mt-4">
                                Floor Type:
                            </label>
                            <select
                                id="floorType"
                                value={floorType}
                                onChange={(e) => setFloorType(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                            >
                                <option value="">Select a floor type</option>
                                <option value="Other">Other...</option>
                                {floorPlanFloorTypes.map(type => <option key={type} value={type}>{type}</option>)}
                            </select>
                            {floorType === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom floor type (e.g., 'Mezzanine')"
                                    value={customFloorType}
                                    onChange={(e) => setCustomFloorType(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                                />
                            )}

                            <label htmlFor="fpNumBedrooms" className="block text-base font-medium text-gray-700 mb-2 mt-4">
                                <Bed className="inline-block w-4 h-4 mr-1" /> Number of Bedrooms:
                            </label>
                            <input
                                type="number"
                                id="fpNumBedrooms"
                                placeholder="e.g., 3"
                                value={fpNumBedrooms}
                                onChange={(e) => setFpNumBedrooms(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                            />

                            <label htmlFor="fpNumBathrooms" className="block text-base font-medium text-gray-700 mb-2 mt-4">
                                <Bath className="inline-block w-4 h-4 mr-1" /> Number of Bathrooms:
                            </label>
                            <input
                                type="number"
                                id="fpNumBathrooms"
                                placeholder="e.g., 2"
                                value={fpNumBathrooms}
                                onChange={(e) => setFpNumBathrooms(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                            />

                            <div className="mt-4 space-y-2">
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={fpHasLivingRoom}
                                        onChange={(e) => setFpHasLivingRoom(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    Include Living Room
                                </label>
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={fpHasKitchen}
                                        onChange={(e) => setFpHasKitchen(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    Include Kitchen
                                </label>
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={fpHasGarage}
                                        onChange={(e) => setFpHasGarage(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    <HardHat className="inline-block w-4 h-4 mr-1" /> Include Garage
                                </label>
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={fpHasGarden}
                                        onChange={(e) => setFpHasGarden(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    <Sprout className="inline-block w-4 h-4 mr-1" /> Include Garden
                                </label>
                            </div>

                            <label htmlFor="floorPlanStyle" className="block text-base font-medium text-gray-700 mb-2 mt-4">
                                Floor Plan Style:
                            </label>
                            <select
                                id="floorPlanStyle"
                                value={floorPlanStyle}
                                onChange={(e) => setFloorPlanStyle(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                            >
                                <option value="">Select a style (Optional)</option>
                                <option value="Open Concept">Open Concept</option>
                                <option value="Traditional">Traditional</option>
                                <option value="Modern">Modern</option>
                                <option value="Compact">Compact</option>
                                <option value="Luxury">Luxury</option>
                                <option value="Other">Other...</option>
                            </select>
                            {floorPlanStyle === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom floor plan style"
                                    value={customFloorPlanStyle}
                                    onChange={(e) => setCustomFloorPlanStyle(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                                />
                            )}

                            <label htmlFor="additionalFloorPlanNotes" className="block text-base font-medium text-gray-700 mb-2 mt-4">
                                Additional Notes:
                            </label>
                            <textarea
                                id="additionalFloorPlanNotes"
                                placeholder="e.g., 'L-shaped kitchen', 'Master bedroom with en-suite', 'Large patio area'"
                                value={additionalFloorPlanNotes}
                                onChange={(e) => setAdditionalFloorPlanNotes(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg resize-y min-h-[80px] focus:ring-blue-500 focus:border-blue-500 text-base"
                            ></textarea>
                        </div>
                    )}

                    {/* Virtual Staging Mode (only for Interior) */}
                    {designType === 'Interior' && (
                        <div className="mb-5">
                            <label className="flex items-center text-lg font-medium text-gray-700 mb-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isVirtualStagingMode}
                                    onChange={(e) => setIsVirtualStagingMode(e.target.checked)}
                                    className="form-checkbox h-5 w-5 text-green-600 rounded mr-2"
                                />
                                Virtual Staging Mode (Furnish Empty Room)
                            </label>
                        </div>
                    )}

                    {/* Design Element (only for Interior) */}
                    {designType === 'Interior' && (
                        <div className="mb-5">
                            <label className="block text-lg font-medium text-gray-700 mb-2">
                                <LayoutDashboard className="inline-block mr-2" /> Design Specific Element:
                            </label>
                            <div className="flex flex-wrap gap-3">
                                <button
                                    onClick={() => setDesignElement('Entire Room')}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                                                ${designElement === 'Entire Room'
                                                    ? 'bg-purple-600 text-white shadow-md'
                                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`
                                              }
                                >
                                    Entire Room
                                </button>
                                <button
                                    onClick={() => setDesignElement('Wall')}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                                                ${designElement === 'Wall'
                                                    ? 'bg-purple-600 text-white shadow-md'
                                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`
                                              }
                                >
                                    <Square className="inline-block w-4 h-4 mr-1" /> Wall
                                </button>
                                <button
                                    onClick={() => setDesignElement('Floor')}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                                                ${designElement === 'Floor'
                                                    ? 'bg-purple-600 text-white shadow-md'
                                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`
                                              }
                                >
                                    <Rows3 className="inline-block w-4 h-4 mr-1" /> Floor
                                </button>
                                <button
                                    onClick={() => setDesignElement('Ceiling')}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                                                ${designElement === 'Ceiling'
                                                    ? 'bg-purple-600 text-white shadow-md'
                                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`
                                              }
                                >
                                    <Columns3 className="inline-block w-4 h-4 mr-1" /> Ceiling
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Specific Element Customization Options */}
                    {designType === 'Interior' && designElement === 'Wall' && (
                        <div className="mb-5 mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <label htmlFor="wallColor" className="block text-lg font-medium text-gray-700 mb-2">
                                Wall Color:
                            </label>
                            <select
                                id="wallColor"
                                value={wallColor}
                                onChange={(e) => setWallColor(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                            >
                                <option value="">Select a color</option>
                                <option value="Other">Other...</option> {/* Other option */}
                                {wallColors.map(color => <option key={color} value={color}>{color}</option>)}
                            </select>
                            {wallColor === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom color (e.g., 'Emerald Green')"
                                    value={customWallColor}
                                    onChange={(e) => setCustomWallColor(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                                />
                            )}
                        </div>
                    )}

                    {designType === 'Interior' && designElement === 'Floor' && (
                        <div className="mb-5 mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <label htmlFor="floorMaterial" className="block text-lg font-medium text-gray-700 mb-2">
                                Floor Material:
                            </label>
                            <select
                                id="floorMaterial"
                                value={floorMaterial}
                                onChange={(e) => setFloorMaterial(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                            >
                                <option value="">Select a material</option>
                                <option value="Other">Other...</option> {/* Other option */}
                                {floorMaterials.map(material => <option key={material} value={material}>{material}</option>)}
                            </select>
                            {floorMaterial === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom material (e.g., 'Polished Concrete')"
                                    value={customFloorMaterial}
                                    onChange={(e) => setCustomFloorMaterial(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                                />
                            )}
                            <label htmlFor="floorStyle" className="block text-lg font-medium text-gray-700 mb-2 mt-4">
                                Floor Style:
                            </label>
                            <select
                                id="floorStyle"
                                value={floorStyle}
                                onChange={(e) => setFloorStyle(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                            >
                                <option value="">Select a style</option>
                                <option value="Other">Other...</option> {/* Other option */}
                                {floorStyles.map(style => <option key={style} value={style}>{style}</option>)}
                            </select>
                            {floorStyle === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom style (e.g., 'Large Herringbone')"
                                    value={customFloorStyle}
                                    onChange={(e) => setCustomFloorStyle(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                                />
                            )}
                        </div>
                    )}

                    {designType === 'Interior' && designElement === 'Ceiling' && (
                        <div className="mb-5 mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <label htmlFor="ceilingType" className="block text-lg font-medium text-gray-700 mb-2">
                                Ceiling Type:
                            </label>
                            <select
                                id="ceilingType"
                                value={ceilingType}
                                onChange={(e) => setCeilingType(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                            >
                                <option value="">Select a type</option>
                                <option value="Other">Other...</option> {/* Other option */}
                                {ceilingTypes.map(type => <option key={type} value={type}>{type}</option>)}
                            </select>
                            {ceilingType === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom type (e.g., 'Stretch Ceiling')"
                                    value={customCeilingType}
                                    onChange={(e) => setCustomCeilingType(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                                />
                            )}
                            <label htmlFor="ceilingColor" className="block text-lg font-medium text-gray-700 mb-2 mt-4">
                                Ceiling Color:
                            </label>
                            <select
                                id="ceilingColor"
                                value={ceilingColor}
                                onChange={(e) => setCeilingColor(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                            >
                                <option value="">Select a color</option>
                                <option value="Other">Other...</option> {/* Other option */}
                                {wallColors.map(color => <option key={color} value={color}>{color}</option>)} {/* Reusing wall colors for simplicity */}
                            </select>
                            {ceilingColor === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom color (e.g., 'Sky Blue')"
                                    value={customCeilingColor}
                                    onChange={(e) => setCustomCeilingColor(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-purple-500 focus:border-purple-500 text-base"
                                />
                            )}
                        </div>
                    )}

                    {/* Advanced Interior Customization */}
                    {designType === 'Interior' && designElement === 'Entire Room' && (
                        <div className="mb-5 mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <h3 className="text-lg font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <Sofa className="w-5 h-5" /> Advanced Interior Options:
                            </h3>
                            <label htmlFor="furnitureStyle" className="block text-base font-medium text-gray-700 mb-2">
                                Furniture Style:
                            </label>
                            <select
                                id="furnitureStyle"
                                value={furnitureStyle}
                                onChange={(e) => setFurnitureStyle(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                            >
                                <option value="">Select a furniture style (Optional)</option>
                                <option value="Other">Other...</option>
                                {furnitureStyles.map(style => <option key={style} value={style}>{style}</option>)}
                            </select>
                            {furnitureStyle === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom furniture style (e.g., 'Art Deco')"
                                    value={customFurnitureStyle}
                                    onChange={(e) => setCustomFurnitureStyle(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                                />
                            )}

                            <label htmlFor="lightingFixture" className="block text-base font-medium text-gray-700 mb-2 mt-4">
                                Lighting Fixtures:
                            </label>
                            <select
                                id="lightingFixture"
                                value={lightingFixture}
                                onChange={(e) => setLightingFixture(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                            >
                                <option value="">Select lighting fixtures (Optional)</option>
                                <option value="Other">Other...</option>
                                {lightingFixtures.map(fixture => <option key={fixture} value={fixture}>{fixture}</option>)}
                            </select>
                            {lightingFixture === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom lighting (e.g., 'Industrial Pendants')"
                                    value={customLightingFixture}
                                    onChange={(e) => setCustomLightingFixture(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                                />
                            )}
                        </div>
                    )}

                    {/* Advanced Exterior Customization */}
                    {designType === 'Exterior' && (
                        <div className="mb-5 mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <h3 className="text-lg font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <TreePine className="w-5 h-5" /> Advanced Exterior Options:
                            </h3>
                            <label htmlFor="landscapingStyle" className="block text-base font-medium text-gray-700 mb-2">
                                Landscaping Style:
                            </label>
                            <select
                                id="landscapingStyle"
                                value={landscapingStyle}
                                onChange={(e) => setLandscapingStyle(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                            >
                                <option value="">Select a landscaping style (Optional)</option>
                                <option value="Other">Other...</option>
                                {landscapingStyles.map(style => <option key={style} value={style}>{style}</option>)}
                            </select>
                            {landscapingStyle === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom landscaping style (e.g., 'Cactus Garden')"
                                    value={customLandscapingStyle}
                                    onChange={(e) => setCustomLandscapingStyle(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                                />
                            )}

                            <label htmlFor="roofType" className="block text-base font-medium text-gray-700 mb-2 mt-4">
                                Roof Type:
                            </label>
                            <select
                                id="roofType"
                                value={roofType}
                                onChange={(e) => setRoofType(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                            >
                                <option value="">Select a roof type (Optional)</option>
                                <option value="Other">Other...</option>
                                {roofTypes.map(type => <option key={type} value={type}>{type}</option>)}
                            </select>
                            {roofType === 'Other' && (
                                <input
                                    type="text"
                                    placeholder="Type custom roof type (e.g., 'Green Roof')"
                                    value={customRoofType}
                                    onChange={(e) => setCustomRoofType(e.target.value)}
                                    className="w-full p-3 mt-2 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500 text-base"
                                />
                            )}
                        </div>
                    )}

                    {/* Plot Specific Options */}
                    {designType === 'Plot' && (
                        <div className="mb-5 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h3 className="text-lg font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <Building className="w-5 h-5" /> Building Details:
                            </h3>
                            <label htmlFor="numFloorsPlot" className="block text-base font-medium text-gray-700 mb-2">
                                Number of Floors (incl. Ground):
                            </label>
                            <input
                                type="number"
                                id="numFloorsPlot"
                                placeholder="e.g., 2"
                                value={numFloorsPlot}
                                onChange={(e) => setNumFloorsPlot(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-base"
                            />

                            <div className="mt-4 space-y-2">
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={hasBalcony}
                                        onChange={(e) => setHasBalcony(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    <Tally3 className="inline-block w-5 h-5 mr-1" /> Include Balcony
                                </label>
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={hasParking}
                                        onChange={(e) => setHasParking(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    <Car className="inline-block w-5 h-5 mr-1" /> Include Parking Area
                                </label>
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={hasPool}
                                        onChange={(e) => setHasPool(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    <Umbrella className="inline-block w-5 h-5 mr-1" /> Include Swimming Pool
                                </label>
                                <label className="flex items-center text-base font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={hasRooftopTerrace}
                                        onChange={(e) => setHasRooftopTerrace(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-blue-600 rounded mr-2"
                                    />
                                    <HomeIconOutline className="inline-block w-5 h-5 mr-1" /> Include Rooftop Terrace
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Design Style (applies to all types) */}
                    <div className="mb-5">
                        <label htmlFor="designStyle" className="block text-lg font-medium text-gray-700 mb-2">
                            <Sparkles className="inline-block mr-2" /> Preferred Style:
                        </label>
                        <select
                            id="designStyle"
                            value={designStyle}
                            onChange={(e) => setDesignStyle(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                        >
                            <option value="">Select a style (Optional)</option>
                            {designType === 'Interior'
                                ? interiorStyles.map(style => <option key={style} value={style}>{style}</option>)
                                : designType === 'Exterior'
                                    ? exteriorStyles.map(style => <option key={style} value={style}>{style}</option>)
                                    : plotBuildingStyles.map(style => <option key={style} value={style}>{style}</option>)
                            }
                        </select>
                    </div>

                    {/* Color Palette (applies to all types) */}
                    <div className="mb-5">
                        <label htmlFor="colorPalette" className="block text-lg font-medium text-gray-700 mb-2">
                            <Palette className="inline-block mr-2" /> Color Palette:
                        </label>
                        <select
                            id="colorPalette"
                            value={colorPalette}
                            onChange={(e) => setColorPalette(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                        >
                            <option value="">Select a color palette (Optional)</option>
                            {commonColorPalettes.map(palette => <option key={palette} value={palette}>{palette}</option>)}
                        </select>
                    </div>

                    {/* Materials (applies to all types) */}
                    <div className="mb-5">
                        <label htmlFor="materials" className="block text-lg font-medium text-gray-700 mb-2">
                            <Box className="inline-block mr-2" /> Key Materials:
                        </label>
                        <select
                            id="materials"
                            value={materials}
                            onChange={(e) => setMaterials(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 text-base"
                        >
                            <option value="">Select materials (Optional)</option>
                            {commonMaterials.map(material => <option key={material} value={material}>{material}</option>)}
                        </select>
                    </div>

                    {/* Generate Button */}
                    <button
                        onClick={handleGenerateDesign}
                        disabled={loading || !apiKey || (designType !== 'Floor Plan Design' && !selectedImage) || !isAuthReady || userCredits < 3 || (designType === 'Plot' && !numFloorsPlot) || (designType === 'Floor Plan Design' && (!length || !width || !floorType || (!fpNumBedrooms && !fpNumBathrooms && !fpHasLivingRoom && !fpHasKitchen && !fpHasGarage && !fpHasGarden && !additionalFloorPlanNotes.trim())))}
                        className={`w-full py-3 px-6 rounded-full text-xl font-bold transition-all duration-300
                                    flex items-center justify-center gap-2
                                    ${loading || !apiKey || (designType !== 'Floor Plan Design' && !selectedImage) || !isAuthReady || userCredits < 3 || (designType === 'Plot' && !numFloorsPlot) || (designType === 'Floor Plan Design' && (!length || !width || !floorType || (!fpNumBedrooms && !fpNumBathrooms && !fpHasLivingRoom && !fpHasKitchen && !fpHasGarage && !fpHasGarden && !additionalFloorPlanNotes.trim())))
                                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg hover:from-purple-700 hover:to-indigo-700 transform hover:scale-105'
                                    }`
                                  }
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin w-6 h-6" /> Generating...
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-6 h-6" /> Generate Design ({designType === 'Floor Plan Design' ? 2 : 3} Credits)
                            </>
                        )}
                    </button>
                    {error && <p className="text-red-600 mt-4 text-center">{error}</p>}
                </div>

                {/* Generated Design Output (Image) */}
                {generatedDesignImage && ( // Single image display for Interior/Exterior or current Plot view
                    <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                        <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 mb-4 flex items-center gap-2">
                            <Sparkles className="w-6 h-6" /> Your Zenifex Generated Design
                        </h2>
                        <div className="flex flex-col items-center justify-center gap-4">
                            <img
                                src={generatedDesignImage}
                                alt="Generated Design"
                                className="max-w-full h-auto rounded-lg shadow-md border border-gray-300"
                            />
                            <div className="flex gap-4 flex-wrap justify-center"> {/* Container for buttons */}
                                <button
                                    onClick={() => handleDownloadDesign(generatedDesignImage)}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-full font-semibold
                                               hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
                                >
                                    <Download className="w-5 h-5" /> Download Design
                                </button>
                                {/* Only show Add/Remove for Interior/Exterior/Plot Photo (not Floor Plan Design from scratch) */}
                                {designType !== 'Floor Plan Design' && (
                                    <button
                                        onClick={() => setShowAddRemoveElementModal(true)}
                                        className="px-6 py-2 bg-indigo-600 text-white rounded-full font-semibold
                                                   hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2"
                                        disabled={loading}
                                    >
                                        <PlusSquare className="w-5 h-5" /> Add/Remove Element ({2} Credits)
                                    </button>
                                )}
                                {/* New buttons for Plot additional views */}
                                {designType === 'Plot' && plotFrontViewImage && (
                                    <>
                                        <button
                                            onClick={() => handleGenerateAdditionalPlotView('front')}
                                            className="px-4 py-2 bg-purple-600 text-white rounded-full font-semibold text-sm
                                                       hover:bg-purple-700 transition-colors duration-200 flex items-center gap-1"
                                            disabled={loading || userCredits < 3}
                                        >
                                            Front View (3 Cr)
                                        </button>
                                        <button
                                            onClick={() => handleGenerateAdditionalPlotView('back')}
                                            className="px-4 py-2 bg-purple-600 text-white rounded-full font-semibold text-sm
                                                       hover:bg-purple-700 transition-colors duration-200 flex items-center gap-1"
                                            disabled={loading || userCredits < 3}
                                        >
                                            Back View (3 Cr)
                                        </button>
                                        <button
                                            onClick={() => handleGenerateAdditionalPlotView('side')}
                                            className="px-4 py-2 bg-purple-600 text-white rounded-full font-semibold text-sm
                                                       hover:bg-purple-700 transition-colors duration-200 flex items-center gap-1"
                                            disabled={loading || userCredits < 3}
                                        >
                                            Side View (3 Cr)
                                        </button>
                                        <button
                                            onClick={() => handleGenerateAdditionalPlotView('top')}
                                            className="px-4 py-2 bg-purple-600 text-white rounded-full font-semibold text-sm
                                                       hover:bg-purple-700 transition-colors duration-200 flex items-center gap-1"
                                            disabled={loading || userCredits < 3}
                                        >
                                            Top View (3 Cr)
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Remove Element Modal */}
            {showAddRemoveElementModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="close-button" onClick={() => setShowAddRemoveElementModal(false)}>
                            &times;
                        </button>
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Add/Remove Element</h2>
                        <p className="text-gray-700 mb-4 text-center">
                            Describe what you want to add or remove from the current design.
                        </p>
                        <textarea
                            className="w-full p-3 border border-gray-300 rounded-lg mb-4 resize-y min-h-[100px] focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="e.g., 'Remove the large painting on the left wall', or 'Add a modern standing lamp next to the sofa'"
                            value={addRemovePrompt}
                            onChange={(e) => setAddRemovePrompt(e.target.value)}
                        ></textarea>
                        <button
                            onClick={handleAddRemoveElement}
                            disabled={loading || !addRemovePrompt.trim() || userCredits < 2} // Changed cost to 2
                            className={`w-full py-3 px-6 rounded-full text-xl font-bold transition-all duration-300
                                        flex items-center justify-center gap-2
                                        ${loading || !addRemovePrompt.trim() || userCredits < 2
                                            ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                            : 'bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 transform hover:scale-105'
                                        }`
                                      }
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin w-6 h-6" /> Applying Changes...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-6 h-6" /> Apply Changes (2 Credits)
                                </>
                            )}
                        </button>
                        {error && <p className="text-red-600 mt-4 text-center">{error}</p>}
                    </div>
                </div>
            )}
             {isCameraOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg mb-4"></video>
                        <div className="flex justify-between gap-4">
                            <button onClick={handleCloseCamera} className="px-6 py-2 bg-gray-300 text-gray-800 rounded-full font-semibold hover:bg-gray-400 transition-colors duration-200">Close</button>
                            <button onClick={handleCapturePhoto} className="px-6 py-2 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition-colors duration-200">Capture Photo</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;
